import { Link, useLocation } from "react-router-dom";
import { LayoutGrid, Heart, Ticket, User, ShoppingBag } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useCart } from "@/contexts/CartContext";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";

interface NavItem {
  id: string;
  href: string;
  icon: React.ElementType;
  label: string;
  isCart?: boolean;
}

function getActiveId(pathname: string): string | null {
  if (pathname.startsWith("/favoritos")) return "favorites";
  if (pathname.startsWith("/meus-ingressos") || pathname.startsWith("/meus-certificados")) return "tickets";
  if (pathname.startsWith("/carrinho")) return "cart";
  if (pathname.startsWith("/meu-perfil") || pathname.startsWith("/notificacoes")) return "profile";
  if (pathname.startsWith("/eventos") || pathname.startsWith("/cidades")) return "catalog";
  return null;
}

export function MobileBottomNav() {
  const { t } = useTranslation("common");
  const { user } = useAuth();
  const { itemCount } = useCart();
  const location = useLocation();

  // Only show for logged-in users
  if (!user) {
    return null;
  }

  if (["/entrar", "/auth/callback", "/recuperar-senha"].includes(location.pathname)) return null;

  // Hide on admin/producer panels
  if (location.pathname.startsWith("/admin") || location.pathname.startsWith("/producer") || location.pathname.startsWith("/produtor")) {
    return null;
  }

  // Hide on checkout flow
  if (location.pathname.startsWith("/checkout")) {
    return null;
  }

  // Hide on staff portal flow
  if (location.pathname.startsWith("/staff")) {
    return null;
  }

  const baseNavItems: NavItem[] = [
    { id: "catalog", href: "/eventos", icon: LayoutGrid, label: t("mobileNav.items.catalog") },
    { id: "favorites", href: "/favoritos", icon: Heart, label: t("mobileNav.items.favorites") },
    { id: "cart", href: "/carrinho", icon: ShoppingBag, label: t("mobileNav.items.cart"), isCart: true },
    { id: "tickets", href: "/meus-ingressos", icon: Ticket, label: t("mobileNav.items.tickets") },
    { id: "profile", href: "/meu-perfil", icon: User, label: t("mobileNav.items.profile") },
  ];

  const navItems = baseNavItems.map((item) =>
    item.id === "profile" ? { ...item, href: "/meu-perfil" } : item
  );

  const activeId = getActiveId(location.pathname);

  return (
    <nav
      className="lg:hidden fixed bottom-0 left-0 right-0 z-50 bg-background/60 backdrop-blur-xl border-t border-border/50 transition-all duration-300 pointer-events-auto"
      style={{ paddingBottom: "env(safe-area-inset-bottom, 12px)" }}
      role="navigation"
      aria-label={t("mobileNav.ariaLabel")}
    >
      <div className="flex items-center justify-around h-16 pt-1 px-2">
        {navItems.map((item) => {
          const isActive = activeId === item.id;
          const Icon = item.icon;

          // Cart - highlighted center button
          if (item.isCart) {
            return (
              <Link
                key={item.id}
                to={item.href}
                className="relative flex flex-col items-center flex-1 touch-manipulation group"
                aria-label={itemCount > 0 ? `${item.label}${t("mobileNav.itemsCountSuffix", { count: itemCount })}` : item.label}
              >
                <div
                  className={cn(
                    "flex items-center justify-center w-12 h-12 rounded-2xl shadow-lg transition-all transform duration-300",
                    isActive 
                      ? "bg-primary text-primary-foreground scale-105 shadow-primary/30" 
                      : "bg-muted/80 text-muted-foreground group-active:scale-90"
                  )}
                >
                  <Icon className={cn("h-6 w-6", isActive && "animate-pulsate")} strokeWidth={2.5} />
                  {itemCount > 0 && (
                    <span
                      className="absolute -top-1.5 -right-1.5 min-w-[20px] h-5 flex items-center justify-center px-1.5 text-[10px] font-black bg-accent text-accent-foreground rounded-full shadow-md animate-in zoom-in-50"
                      aria-hidden="true"
                    >
                      {itemCount > 99 ? "99+" : itemCount}
                    </span>
                  )}
                </div>
                <span className={cn(
                  "text-[9px] font-bold mt-1 uppercase tracking-tighter transition-colors",
                  isActive ? "text-primary" : "text-muted-foreground/60"
                )}>
                  {item.label}
                </span>
              </Link>
            );
          }

          // Regular nav items
          return (
            <Link
              key={item.id}
              to={item.href}
              className={cn(
                "flex flex-col items-center justify-center gap-1 flex-1 py-1 transition-all touch-manipulation group",
                "active:scale-95"
              )}
              aria-current={isActive ? "page" : undefined}
            >
              <div
                className={cn(
                  "flex items-center justify-center w-10 h-10 rounded-xl transition-all duration-300 group-hover:bg-muted/50",
                  isActive
                    ? "bg-primary/10 text-primary"
                    : "text-muted-foreground/70"
                )}
              >
                <Icon className={cn("h-5 w-5 transition-all", isActive && "scale-110")} strokeWidth={isActive ? 2.5 : 2} />
              </div>
              <span
                className={cn(
                  "text-[10px] font-bold transition-all duration-300 tracking-tight",
                  isActive ? "text-primary translate-y-0" : "text-muted-foreground/60"
                )}
              >
                {item.label}
              </span>
            </Link>
          );
        })}
      </div>
    </nav>
  );
}
